import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useQuery } from '@powersync/react';
import { useApp, useClinicCtx } from '@/app/state';
import { Button, Card, Empty, Input, Modal, Pill } from '@/components/ui';
import { searchPatients, ageLabel } from '@/features/patients/repo';
import { activeTokensToday, issueToken, setSessionStatus, type ActiveToken } from '@/features/queue/repo';
import type { DoctorRow } from '@/lib/db/schema';
import type { PatientIndexRow, SessionRow } from '@/lib/db/schema';
import { formatPhoneLocal } from '@/lib/myanmar/phone';
import { toMyanmarDigits } from '@/lib/myanmar/digits';
import { RegisterPatientForm } from '@/features/patients/RegisterPatientForm';
import { TokenSlip } from '@/features/queue/TokenSlip';
import { normalizePhone } from '@/lib/myanmar/phone';
import { toAsciiDigits } from '@/lib/myanmar/digits';
import { getAutoPrint } from '@/app/device';

type SessionWithDoctor = SessionRow & { doctor_name_en: string | null; doctor_name_my: string | null; waiting: number };

export function ReceptionPage() {
  const { t, i18n } = useTranslation();
  const my = i18n.language === 'my';
  const ctx = useClinicCtx();
  const { clinic } = useApp();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<PatientIndexRow[]>([]);
  const [registering, setRegistering] = useState<false | { phone?: string; name_my?: string; name_en?: string }>(false);
  const [tokenFor, setTokenFor] = useState<PatientIndexRow | null>(null);
  const [slip, setSlip] = useState<{ label: string; patient: PatientIndexRow; session: SessionWithDoctor } | null>(null);
  const [queuedToday, setQueuedToday] = useState<Record<string, ActiveToken[]>>({});

  // Live re-run when patients change locally or via sync.
  const { data: tick } = useQuery('select count(*) as n, max(updated_at) as u from patient_index where clinic_id = ?', [ctx.clinicId]);
  useEffect(() => { void searchPatients(ctx.db, ctx.clinicId, q).then(setResults); }, [q, ctx.db, ctx.clinicId, tick]);

  // Which of the listed patients already hold a queue number today?
  const { data: tokenTick } = useQuery('select count(*) as n, max(updated_at) as u from tokens where clinic_id = ?', [ctx.clinicId]);
  useEffect(() => {
    let alive = true;
    (async () => {
      const map: Record<string, ActiveToken[]> = {};
      for (const p of results) if (p.patient_id) map[p.patient_id] = await activeTokensToday(ctx.db, p.patient_id);
      if (alive) setQueuedToday(map);
    })();
    return () => { alive = false; };
  }, [results, ctx.db, tokenTick]);

  const { data: sessions } = useQuery<SessionWithDoctor>(
    `select s.*, d.name_en as doctor_name_en, d.name_my as doctor_name_my,
            (select count(*) from tokens t where t.session_id = s.id and t.status in ('waiting','called')) as waiting
     from sessions s join doctors d on d.id = s.doctor_id where s.clinic_id = ? and s.session_date = date('now','localtime') and s.status in ('open','paused','planned') order by s.start_time`, [ctx.clinicId]);

  const { data: doctors } = useQuery<DoctorRow>('select * from doctors where clinic_id = ? and active = 1 order by name_en', [ctx.clinicId]);
  const openSessions = (sessions ?? []).filter((s) => s.status === 'open');

  async function onIssue(session: SessionWithDoctor, priority: boolean, reason: string, patient = tokenFor) {
    if (!patient) return;
    const r = await issueToken(ctx, { session_id: session.id, patient_id: patient.patient_id!, priority, priority_reason: reason || null });
    setSlip({ label: r.label, patient, session });
    setTokenFor(null);
    setQueuedToday((q) => ({ ...q, [patient.patient_id!]: [] }));   // refreshed by the effect below
  }

  /** One tap when exactly one doctor is open; otherwise ask which doctor.
   *  If the patient already holds a number for that doctor, show the dialog so
   *  reception sees the existing number rather than silently re-issuing. */
  async function queuePatient(p: PatientIndexRow) {
    const held = await activeTokensToday(ctx.db, p.patient_id!);
    if (openSessions.length === 1 && !held.some((h) => h.session_id === openSessions[0]!.id)) {
      void onIssue(openSessions[0]!, false, '', p);
    } else {
      setTokenFor(p);
    }
  }

  async function startClinic(doctorId: string) {
    const planned = (sessions ?? []).find((s) => s.doctor_id === doctorId && s.status === 'planned');
    if (planned) { await setSessionStatus(ctx, planned.id, 'open'); return; }
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0') + ':' + String(now.getMinutes()).padStart(2, '0');
    const { createSession, todayLocal } = await import('@/features/queue/repo');
    await createSession(ctx, { doctor_id: doctorId, session_date: todayLocal(), start_time: hh, end_time: '23:59', open: true });
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
      <section>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Input autoFocus placeholder={t('patient.searchHint')} value={q} onChange={(e) => setQ(e.target.value)} className="flex-1 min-w-60 text-lg" inputMode="search" />
          <Button variant="primary" onClick={() => setRegistering({})}>＋ {t('patient.new')}</Button>
        </div>
        {results.length === 0 ? (
          <Empty>
            <div>{t('patient.noResults')}</div>
            {(() => {
              const raw = q.trim();
              if (!raw) return null;
              const phone = /^[0-9၀-၉+\s-]{7,}$/.test(raw) ? normalizePhone(raw) : null;
              if (phone) return <Button variant="primary" className="mt-3" onClick={() => setRegistering({ phone: toAsciiDigits(raw) })}>{t('patient.registerWithPhone', { phone: raw })}</Button>;
              const isMy = /[က-႟]/.test(raw);
              return <Button variant="primary" className="mt-3" onClick={() => setRegistering(isMy ? { name_my: raw } : { name_en: raw })}>{t('patient.registerWithName', { name: raw })}</Button>;
            })()}
          </Empty>
        ) : (
          <ul className="divide-y divide-rule overflow-hidden rounded-xl border border-rule bg-surface">
            {results.map((p) => {
              const age = ageLabel(p);
              const phones = (JSON.parse(p.phones ?? '[]') as string[]);
              return (
                <li key={p.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <Link to={`/patients/${p.patient_id}`} className="text-lg font-bold hover:underline">
                        {p.honorific && p.honorific !== 'none' ? t(`honorific.${p.honorific}`) + ' ' : ''}{my ? (p.name_my ?? p.name_en) : (p.name_en ?? p.name_my)}
                      </Link>
                      {p.name_my && p.name_en && <span className="text-ink-2">{my ? p.name_en : p.name_my}</span>}
                    </div>
                    <div className="flex flex-wrap gap-x-3 text-sm text-ink-2">
                      <span className="font-mono">{p.patient_no}</span>
                      {age && <span>{my ? `${toMyanmarDigits(age.years)} ${t('patient.ageYears')}` : `${age.years} y`}{age.years < 5 ? (my ? ` ${toMyanmarDigits(age.months)} ${t('patient.ageMonths')}` : ` ${age.months} m`) : ''}</span>}
                      {phones[0] && <span>{formatPhoneLocal(phones[0])}</span>}
                      {p.nrc && <span>{p.nrc}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {(queuedToday[p.patient_id ?? ''] ?? []).map((h) => (
                      <Pill key={h.id} tone="warn">{t('queue.alreadyQueuedToday')} · {my ? toMyanmarDigits(h.token_label) : h.token_label}</Pill>
                    ))}
                    <Button variant="primary" onClick={() => queuePatient(p)} disabled={openSessions.length === 0 && (sessions ?? []).length === 0}>{t('queue.issueToken')}</Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <aside className="grid gap-3 self-start">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">{t('common.today')} · {t('queue.session')}</h2>
        {openSessions.length === 0 && (
          <Card className="grid gap-2">
            <div className="text-ink-2">{t('queue.noSessions')}</div>
            {(doctors ?? []).map((d) => (
              <Button key={d.id} variant="primary" onClick={() => startClinic(d.id)}>{t('queue.startSession', { doctor: my ? (d.name_my ?? d.name_en) : (d.name_en ?? d.name_my) })}</Button>
            ))}
          </Card>
        )}
        {(sessions ?? []).map((s) => (
          <Card key={s.id} className="flex items-center justify-between gap-2">
            <div>
              <div className="font-bold">{my ? (s.doctor_name_my ?? s.doctor_name_en) : (s.doctor_name_en ?? s.doctor_name_my)}</div>
              <div className="text-sm text-ink-2">{s.start_time}–{s.end_time}{s.room ? ` · ${t('queue.room')} ${s.room}` : ''}</div>
            </div>
            <div className="text-right">
              <Pill tone={s.status === 'open' ? 'accent' : 'neutral'}>{t(`session.status.${s.status}`)}</Pill>
              <div className="mt-1 text-sm text-ink-2">{t('queue.waiting')}: <b>{s.waiting}</b></div>
            </div>
          </Card>
        ))}
      </aside>

      <Modal open={!!registering} onClose={() => setRegistering(false)} title={t('patient.register')} wide>
        {registering && <RegisterPatientForm prefix={clinic?.patient_no_prefix ?? 'KLX'} prefill={registering} onDone={(p) => { setRegistering(false); setQ(''); if (p) queuePatient(p); }} />}
      </Modal>

      <Modal open={!!tokenFor} onClose={() => setTokenFor(null)} title={t('queue.issueToken')}>
        {tokenFor && <IssueTokenDialog patient={tokenFor} sessions={(sessions ?? []).filter((s) => s.status !== 'closed')} held={queuedToday[tokenFor.patient_id ?? ''] ?? []} onIssue={onIssue} />}
      </Modal>

      <Modal open={!!slip} onClose={() => setSlip(null)} title={t('queue.tokenSlip')}>
        {slip && <TokenSlip label={slip.label} patient={slip.patient} session={slip.session} clinicName={(my && clinic?.name_my) || clinic?.name || ''} autoPrint={getAutoPrint()} onClose={() => setSlip(null)} />}
      </Modal>
    </div>
  );
}

function IssueTokenDialog({ patient, sessions, held, onIssue }: { patient: PatientIndexRow; sessions: SessionWithDoctor[]; held: ActiveToken[]; onIssue: (s: SessionWithDoctor, priority: boolean, reason: string) => Promise<void> }) {
  const { t, i18n } = useTranslation();
  const my = i18n.language === 'my';
  const [priority, setPriority] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  if (sessions.length === 0) return <Empty>{t('queue.noSessions')}</Empty>;
  return (
    <div className="grid gap-3">
      <p className="text-ink-2">{patient.honorific && patient.honorific !== 'none' ? t(`honorific.${patient.honorific}`) + ' ' : ''}<b>{my ? (patient.name_my ?? patient.name_en) : (patient.name_en ?? patient.name_my)}</b> · {patient.patient_no}</p>
      <label className="flex items-center gap-2"><input type="checkbox" checked={priority} onChange={(e) => setPriority(e.target.checked)} className="h-5 w-5" /> {t('queue.priority')}</label>
      {priority && <Input placeholder={t('queue.priorityReason')} value={reason} onChange={(e) => setReason(e.target.value)} />}
      {held.length > 0 && (
        <div className="rounded-lg border border-warn/40 bg-warn-soft px-3 py-2 text-sm text-warn">
          {held.map((h) => (
            <div key={h.id}>{t('queue.alreadyQueued', { label: my ? toMyanmarDigits(h.token_label) : h.token_label, doctor: my ? (h.doctor_name_my ?? h.doctor_name_en) : (h.doctor_name_en ?? h.doctor_name_my) })}</div>
          ))}
        </div>
      )}
      <div className="grid gap-2">
        {sessions.map((s) => {
          const existing = held.find((h) => h.session_id === s.id);
          return (
            <Button key={s.id} variant={existing ? 'secondary' : 'primary'} disabled={busy || !!existing || (priority && !reason.trim())}
              title={existing ? t('queue.sameDoctorTwice') : undefined}
              onClick={async () => { setBusy(true); try { await onIssue(s, priority, reason); } finally { setBusy(false); } }} className="justify-between">
              <span>{my ? (s.doctor_name_my ?? s.doctor_name_en) : (s.doctor_name_en ?? s.doctor_name_my)}</span>
              <span className="text-sm opacity-80">
                {existing ? t('queue.keepExisting', { label: my ? toMyanmarDigits(existing.token_label) : existing.token_label }) : `${s.start_time} · ${t('queue.waiting')} ${s.waiting}`}
              </span>
            </Button>
          );
        })}
      </div>
    </div>
  );
}
